<script setup lang="ts">
  import { components } from '@/cypaths';
  import { computed, defineComponent, markRaw, onMounted, ref } from 'vue';
  import { StatusStateMap } from '@/components/common/StatusIcon/statusIcon.ts';
  import warningIconSvg from '@/assets/images/status-warning-small.svg';
  import errorIconSvg from '@/assets/images/status-error-small.svg';
  import checkIconSvg from '@/assets/images/svgSprite/check-round.svg';
  import type {
    ExposedNotificationBoxProps,
    NotificationBoxProps, NotificationBoxStatus,
  } from '@/components/common/NotificationBox/notificationBox.ts';
  import IconButton from '@/components/common/formElements/buttons/IconButton.vue';
  import { useI18n } from 'vue-i18n';
  import StatusIcon from '@/components/common/StatusIcon/StatusIcon.vue';
  import { isTranslatableLabel } from '@/utils/i18n.ts';
  import useLocale from '@/composables/useLocale.ts';
  import { NOTIFICATION_BOX_CONTENT, NOTIFICATION_BOX_TITLE } from '@/cypaths/cyComponents.ts';

  const statusTypeToClassesMap: Record<NotificationBoxStatus, string> = {
    [StatusStateMap.Warning]: 'border-(--color-yellow) bg-(--color-yellow-extralight)',
    [StatusStateMap.Error]: 'border-(--color-red) bg-(--color-red-extralight)',
    [StatusStateMap.Success]: 'border-(--color-green) bg-(--color-green-extralight)',
  };

  const statusTypeToIconMap: Record<NotificationBoxStatus, string> = {
    [StatusStateMap.Warning]: warningIconSvg,
    [StatusStateMap.Error]: errorIconSvg,
    [StatusStateMap.Success]: checkIconSvg,
  };

  defineOptions({ name: 'NotificationBox', inheritAttrs: false });

  const emits = defineEmits<{
    close: [];
  }>();

  const { compact = false, vertical = false, ...props } = defineProps<NotificationBoxProps>();

  const headingElement = ref<HTMLHeadingElement>();
  const notificationElement = ref<HTMLElement>();
  const locale = useLocale();
  const { t } = useI18n();

  const contentComponent = computed(() => {
    if (!props.content) {
      return;
    }

    if (typeof props.content === 'string' || isTranslatableLabel(props.content)) {
      return defineComponent(() => () => isTranslatableLabel(props.content)
        ? props.content[locale.value]
        : props.content || '');
    } else {
      return markRaw(props.content);
    }
  });

  const translatedTitle = computed<string | undefined>(() => {
    return props.title
      ? isTranslatableLabel(props.title)
        ? props.title[locale.value]
        : props.title
      : undefined;
  });

  const notificationTitle = computed<string | undefined>(() => {
    return translatedTitle.value;
  });

  const statusId = computed<string | undefined>(() => props.id);

  function focus(): void {
    if (headingElement.value) {
      headingElement.value.focus();
      return;
    }
    if (notificationElement.value) {
      notificationElement.value.focus();
    }
  }

  onMounted(() => {
    if (props.focusOnMount) {
      focus();
    }
  });

  defineExpose<ExposedNotificationBoxProps>({ focus });
</script>

<template>
  <div
    v-if="status === StatusStateMap.Success && compact"
    data-testid="notification-success"
    :data-cy="components.NOTIFICATION_BOX"
    :data-status="status"
    :data-name="statusId"
    class="group colored-bg md:container flex w-full items-center gap-6 rounded border border-green bg-green-extralight p-4 md:flex-row md:p-8"
    :class="{'vertical-layout': vertical}"
  >
    <p
      ref="notificationElement"
      data-testid="notification-success-content"
      :data-cy="NOTIFICATION_BOX_CONTENT"
      tabindex="0"
      class="grow"
    >
      <slot>
        <component :is="contentComponent" />
      </slot>
    </p>
    <IconButton
      :image-spacing="false"
      class="self-start text-[#349A11]"
      :label="t('generic.close.ariaLabel')"
      icon="close-round"
      @click="emits('close')"
    />
  </div>
  <template v-else>
    <div
      v-if="compact"
      v-bind="$attrs"
      data-testid="message-box"
      class="group colored-bg flex w-full gap-4 overflow-hidden rounded border-2 p-4 pb-5"
      :class="[
        statusTypeToClassesMap[status],
        {'vertical-layout': vertical},
        $attrs['class']
      ]"
      :data-cy="components.NOTIFICATION_BOX"
      :data-status="status"
      :data-name="statusId"
    >
      <div class="flex-none">
        <slot name="icon">
          <component :is="statusTypeToIconMap[status]" />
        </slot>
      </div>
      <div
        ref="notificationElement"
        data-testid="message-box-focusable"
        tabindex="-1"
        class="flex flex-col gap-2 overflow-hidden wrap-break-word text-left focus-visible:outline-hidden md:gap-4"
      >
        <span
          v-if="notificationTitle"
          :data-cy="NOTIFICATION_BOX_TITLE"
          class="h2 text-base! font-bold"
        >
          {{ notificationTitle }}
        </span>
        <slot>
          <div
            v-if="contentComponent"
            :data-cy="NOTIFICATION_BOX_CONTENT"
            data-testid="message-box-content"
          >
            <component :is="contentComponent" />
          </div>
        </slot>
      </div>
    </div>
    <div
      v-else
      ref="notificationElement"
      data-testid="status-box"
      class="group colored-bg w-full relative flex flex-col gap-4 rounded-box border-2 px-5 py-10 md:gap-8 md:px-8 md:py-8 md:mx-0"
      :class="[
        statusTypeToClassesMap[status],
        {'md:flex-row': !vertical, 'vertical-layout': vertical},
        $attrs['class']
      ]"
      :data-cy="components.NOTIFICATION_BOX"
      v-bind="$attrs"
      :data-status="status"
      :data-name="statusId"
      tabindex="-1"
    >
      <StatusIcon
        class="absolute left-1/2 top-0 max-w-1/2 -translate-x-1/2 -translate-y-1/2"
        :class="{'md:relative md:left-0 md:top-0 md:flex-none md:-translate-x-0 md:-translate-y-0 md:self-start': !vertical}"
        :status="status"
        size="large"
      />
      <div class="flex flex-col gap-4 overflow-hidden">
        <h2
          v-if="notificationTitle"
          ref="headingElement"
          data-testid="status-box-title"
          :data-cy="NOTIFICATION_BOX_TITLE"
          tabindex="-1"
          :class="{'text-center': vertical}"
        >
          {{ notificationTitle }}
        </h2>
        <p
          :data-cy="NOTIFICATION_BOX_CONTENT"
          data-testid="status-box-content"
        >
          <slot>
            <component :is="contentComponent" />
          </slot>
        </p>
      </div>
    </div>
  </template>
</template>
